import * as React from 'react'
import { jsx } from '@emotion/react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import moment from 'moment'
import queryString from 'query-string'
import { Helmet } from 'react-helmet'
import Loadable from '@react-loadable/revised'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { useLocalStorage } from 'react-use'

import BusyOverlay from './common/BusyOverlay'
import { TECH_READINESS_RELEASE_DATE } from './tech-readiness/tech-readiness-utils'
import NoTeamLandingPage from './NoTeamLandingPage'

import AlfiAttackDetailsPage from 'components/alfi/details/AlfiAttackDetailsPage'
// Attacks
import InfraAttackDetailsPage from 'components/attacks/details/InfraAttackDetailsPage'
import LoadableLoading from 'components/common/LoadableLoading'
import Dashboard from 'components/dashboard/Dashboard'
import Footer from 'components/Footer'
import Nav from 'components/navigation/Nav'
import Profile from 'components/profile/Profile'
import Schedules from 'components/schedules/Schedules'
import TrackedGremlinRoute from 'components/TrackedGremlinRoute'
import { NO_TEAM } from 'context/AuthContext'
import useAuth from 'hooks/use-auth'
import {
  prefetchPaginationData,
  useGremlinQuery
} from 'hooks/use-gremlin-query'
import useLongRunningApiData from 'hooks/use-long-running-api-data'
import toast from 'lib/toast'
import { hasSupportAppPermission } from 'support/helpers/utilities'
import theme from 'theme'
import { defaultScenarioTab } from 'utils/session'

const ApiDocs = Loadable({
  loader: () => import(/* webpackChunkName: "ApiDocs" */ 'components/ApiDocs'),
  loading: LoadableLoading
})

// reports
const TeamReport = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "TeamReport" */ 'components/reports/TeamReport'
    ),
  loading: LoadableLoading
})

const CompanyReport = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "Report" */ 'components/reports/company/Report'
    ),
  loading: LoadableLoading
})

const PlanReport = Loadable({
  loader: () =>
    import(/* webpackChunkName: "Report" */ 'components/reports/plan/Report'),
  loading: LoadableLoading
})

const AdminCompanyReport = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CompanyReport" */ 'components/reports/CompanyReport'
    ),
  loading: LoadableLoading
})

const CompanySettings = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CompanySettings" */ 'components/rbac-settings/CompanySettings'
    ),
  loading: LoadableLoading
})

const TeamSettings = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "TeamSettings" */ 'components/rbac-settings/teams/TeamSettings'
    ),
  loading: LoadableLoading
})

const UserPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "UserPage" */ 'components/rbac-settings/users/UserPage'
    ),
  loading: LoadableLoading
})

// New Create Attack Routes
const AlfiAttacksListPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "AlfiAttacksListPage" */ 'components/attacks/list/AlfiAttacksListPage'
    ),
  loading: LoadableLoading
})

const CreateAttackPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CreateAttackPage" */ 'components/attacks/create/CreateAttackPage'
    ),
  loading: LoadableLoading
})

const KubernetesAttackDetailsPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "KubernetesAttackDetailsPage" */ 'components/attacks/details/kubernetes/KubernetesAttackDetailsPage'
    ),
  loading: LoadableLoading
})

const InfraAttackList = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "InfraAttackListPage" */ 'components/attacks/list/InfraAttackListPage'
    ),
  loading: LoadableLoading
})

const ScenariosAttackList = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenariosAttackListPage" */ 'components/attacks/list/ScenariosAttackListPage'
    ),
  loading: LoadableLoading
})

// New Attacks Pages
const CreateScenarios = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CreateScenarioPage" */ 'components/scenarios/create/CreateScenarioPage'
    ),
  loading: LoadableLoading
})

const ExperimentsOverview = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ExperimentsOverview" */ 'components/scenarios/overview/Overview'
    ),
  loading: LoadableLoading
})

const ScenarioDetailsPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenarioDetailsPage" */ 'components/scenarios/detail/ScenarioDetailsPage'
    ),
  loading: LoadableLoading
})

const ScenariosRecommendedDetail = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenarioRecommendedDetail" */ 'components/scenarios/recommended-detail/RecommendedDetail'
    ),
  loading: LoadableLoading
})

const SupportAppLegacy = Loadable({
  loader: () =>
    import(/* webpackChunkName: "SupportAppLegacy" */ 'support/index'),
  loading: LoadableLoading
})

const SupportApp = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SupportApp" */ 'components/support/SupportIndex'
    ),
  loading: LoadableLoading
})

const ScenariosCompanyRecommendedDetail = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenarioCompanyRecommendedDetail" */ 'components/scenarios/recommended-detail/CompanyRecommendedDetail'
    ),
  loading: LoadableLoading
})

const Onboarding = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "Onboarding" */ 'components/onboarding/Onboarding'
    ),
  loading: LoadableLoading
})

const ClientsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ClientsIndex" */ 'components/clients/ClientsIndex'
    ),
  loading: LoadableLoading
})

const ManageClusterAccessPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ManageClusterAccessPage" */ 'components/kubernetes/access-control/ManageClusterAccessPage'
    ),
  loading: LoadableLoading
})

const ServicesIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ServicesIndex" */ 'components/services/ServicesIndex'
    ),
  loading: LoadableLoading
})

const NotFound = Loadable({
  loader: () =>
    import(/* webpackChunkName: "NotFound" */ 'components/NotFound'),
  loading: LoadableLoading
})

export default function AuthenticatedRoutes() {
  const {
    currentTeamId,
    isAdmin,
    companyIsAlfiEnabled,
    hasElevatedCompanyRole,
    hasCompanyPrivilege,
    isFreeEdition,
    currentTeam,
    session,
    RBAC
  } = useAuth()

  const location = useLocation()

  // Feature flags
  const {
    pricingUsage: isPricingUsageEnabled,
    serviceDiscovery: isServicesEnabled
  } = useFlags()

  const isDashboard = ['/', '/dashboard'].includes(location.pathname)

  const hideNavBar = location.pathname.includes('/onboarding')

  const userHasTeams = currentTeamId !== NO_TEAM

  const [hasAccessedOnboarding, setHasAccessedOnboarding] = useLocalStorage(
    'onboardingAccessed',
    false
  )
  const {
    data: teamProfile = {
      fields: [],
      completedByUsers: [],
      skippedByUsers: []
    },
    isLoading: teamProfileLoading
  } = useGremlinQuery(
    `/teams/${currentTeamId}/profile`,
    {},
    {
      enabled: !isFreeEdition && userHasTeams,
      requestInterval: 5 * 60_000
    }
  )

  useLongRunningApiData(userHasTeams)

  React.useEffect(() => {
    // Prefetch services because the api call is slow
    // Cannot use usePrefetchPaginationData due to feature flag condition
    const endpoint = `/prototype/services/paged?teamId=${currentTeamId}&statusType=All&pageSize=20`
    if (userHasTeams && isServicesEnabled) {
      prefetchPaginationData(endpoint, {})
    }
  }, [currentTeamId, isServicesEnabled, userHasTeams])

  let shouldLaunchOnboarding = false
  if (hasAccessedOnboarding) {
    // If the value is true in localStorage keep false value and drop out of conditional
    shouldLaunchOnboarding = false
  } else if (isFreeEdition) {
    // Free edition check
    shouldLaunchOnboarding =
      // User hasn't logged in before
      !session.last_login &&
      // User's current team has NOT finished onboarding
      (currentTeam.onboarding_state === undefined ||
        currentTeam.onboarding_state === 'INCOMPLETE')
  } else if (
    // Implied enterprise
    // Ensure the enterprise team was created after tech readiness release
    moment(currentTeam.created_at).isAfter(moment(TECH_READINESS_RELEASE_DATE))
  ) {
    // Corner case
    // If the user changes teams and the profile has loaded from previous team but contains null values
    if (
      !Array.isArray(teamProfile?.completedByUsers) ||
      !Array.isArray(teamProfile?.skippedByUsers)
    ) {
      shouldLaunchOnboarding = false
    } else {
      // If the user is not listed in survey results as completed or skipped show the onboarding flow
      const completedByUsers = teamProfile?.completedByUsers ?? []
      const skippedByUsers = teamProfile?.skippedByUsers ?? []
      shouldLaunchOnboarding = !completedByUsers
        .concat(skippedByUsers)
        .includes(session.identifier)
    }
  }

  if (!userHasTeams) {
    shouldLaunchOnboarding = false
  }

  // Display loading indicator if team profile GET is loading
  if (teamProfileLoading) return <BusyOverlay shown />

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: `${hideNavBar ? '' : 'var(--nav-width)'} 1fr`,

        [theme.mq[2]]: {
          display: 'grid',
          gridTemplateColumns: `${hideNavBar ? '' : 'var(--nav-width)'} 1fr`
        }
      }}
    >
      {!hideNavBar && <Nav userHasTeams={userHasTeams} />}

      <Helmet>
        {/* Hotjar tracking code */}
        {isFreeEdition && (
          <script>{`
            (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:1726087,hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `}</script>
        )}
      </Helmet>
      <div
        css={{
          display: 'grid',
          gridTemplateRows: '1fr auto'
        }}
      >
        <article
          css={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            marginTop: 'calc(var(--info-banner-height) * -1)',
            paddingTop: 'var(--info-banner-height)',
            backgroundColor: isDashboard && theme.colors.panel[0]
          }}
        >
          {userHasTeams ? (
            <Switch>
              {/*
                IMPORTANT: we need different Switch components here because you CANNOT have Fragments
                inside a Switch component. The Route components must be children of Switch directly!
              */}
              <TrackedGremlinRoute
                loggedIn
                name="Onboarding"
                path="/onboarding"
                render={() => jsx(Onboarding, { setHasAccessedOnboarding })}
              />
              {shouldLaunchOnboarding && (
                <Redirect exact from="*" to="/onboarding" />
              )}
              <TrackedGremlinRoute
                exact
                loggedIn
                component={Dashboard}
                name="Dashboard"
                path={['/', '/dashboard']}
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={InfraAttackList}
                name="Attacks List"
                path="/attacks"
              />
              <TrackedGremlinRoute
                loggedIn
                component={CreateAttackPage}
                name="Create Attack"
                path="/attacks/new/:targetType?"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={InfraAttackList}
                name="ILFI Attacks List"
                path="/attacks/infrastructure"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={AlfiAttacksListPage}
                name="Application Attacks List"
                path="/attacks/application"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={ScenariosAttackList}
                name="Scenarios List"
                path="/attacks/scenarios"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={InfraAttackDetailsPage}
                name="ILFI Attack Details"
                path="/attacks/:id"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={KubernetesAttackDetailsPage}
                name="Kubernetes Attack Details"
                path="/attacks/kubernetes/:id"
              />
              <TrackedGremlinRoute
                loggedIn
                component={Schedules}
                name="Scheduled Attacks List"
                path="/schedules/:tab"
              />
              <Redirect from="/schedules" to="/schedules/attacks" />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={CreateScenarios}
                name="Create Scenario"
                path="/scenarios/new/:targetType?"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                name="Edit Scenario"
                path="/scenarios/edit/:targetType?"
                render={props => <CreateScenarios isEditing {...props} />}
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={ScenariosCompanyRecommendedDetail}
                name="Recommended Company Scenario Details"
                path="/scenarios/shared/company/:scenarioId/:type?"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={ExperimentsOverview}
                name="Scenario Overview"
                path="/scenarios/:tab/company"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={ScenariosRecommendedDetail}
                name="Recommended Scenario Details"
                path="/scenarios/recommended/:slug/:type?"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={ExperimentsOverview}
                name="Scenario Overview"
                path="/scenarios/:tab"
              />
              <Route // We need to evaluate this each time to get the right tab. Redirect doesn't allow for that so we need a Route + Redirect. Using a plain Route so it isn't double counted.
                exact
                component={() => {
                  const tab = defaultScenarioTab()
                  return (
                    <Redirect
                      exact
                      from="/scenarios"
                      to={`/scenarios/${tab}`}
                    />
                  )
                }}
                path="/scenarios"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={ExperimentsOverview}
                name="Status Checks Library"
                path="/scenarios/status-checks/:tab"
              />

              <TrackedGremlinRoute
                loggedIn
                component={ManageClusterAccessPage}
                name="Manage Cluster Access"
                path="/clients/kubernetes/:assetId"
              />

              <TrackedGremlinRoute
                loggedIn
                component={ClientsIndex}
                name="Clients"
                path="/clients/:tab?"
              />

              <TrackedGremlinRoute
                loggedIn
                component={ServicesIndex}
                name="Services"
                path="/services/:tab?"
                shouldRenderRoute={isServicesEnabled}
              />

              <TrackedGremlinRoute
                exact
                loggedIn
                component={UserPage}
                name="User Details"
                path="/settings/users/:userId"
                shouldRenderRoute={hasCompanyPrivilege(
                  RBAC.privileges.company.READ_ALL_USERS
                )}
              />
              {!hasElevatedCompanyRole && (
                <Redirect
                  from="/team-details/:tab?"
                  to={`/settings/team/${currentTeamId}/:tab?`}
                />
              )}
              {/**
               * The Onboarding team wants to use `https://app.gremlin.com/settings/team/current/configuration`
               * as a generic link to access a new user's current team when they log in the first time.
               */}
              <Redirect
                from="/settings/team/current/configuration"
                to={`/settings/team/${currentTeamId}/configuration`}
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={TeamSettings}
                name="Team Settings"
                path="/settings/team/:teamId/:tab?"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={CompanySettings}
                name="Company Settings"
                path="/settings/:tab?"
                shouldRenderRoute={hasElevatedCompanyRole}
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={Profile}
                name="User Profile"
                path="/profile/:tab?"
              />
              <Redirect from="/team-report" to="/reports/team" />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={TeamReport}
                name="Team Report"
                path="/reports/team"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={CompanyReport}
                name="Company Report"
                path="/reports/company"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={PlanReport}
                name="Plan Report"
                path="/reports/plan"
                shouldRenderRoute={isPricingUsageEnabled}
              />
              <TrackedGremlinRoute
                loggedIn
                component={ScenarioDetailsPage}
                name="Scenario Run Details"
                path="/scenarios/detail/:guid/:tab?"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={AlfiAttackDetailsPage}
                name="Application Attack Details"
                path="/attacks/application/:id"
                shouldRenderRoute={companyIsAlfiEnabled}
              />
              <Redirect from="/alfi/:id" to="/attacks/application/:id" />
              <TrackedGremlinRoute
                loggedIn
                component={AdminCompanyReport}
                name="Company Report"
                path="/admin/reports"
                shouldRenderRoute={isAdmin}
              />

              <TrackedGremlinRoute
                exact
                loggedIn
                path="/slack"
                render={() => {
                  const message = queryString.parse(location.search).msg

                  if (message)
                    toast({
                      type: 'info',
                      text: message
                    })

                  return <Redirect to="/settings" />
                }}
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={ApiDocs}
                name="API Docs"
                path="/api"
              />

              {/* these make it so the user doesn't get a 404 if they hit the back button after logging in */}
              <Redirect from="/login" to="/" />
              <Redirect from="/users/sso/*" to="/" />

              {/* TODO rename legacy support routes /support/legacy/ and change the new routes to /support */}
              <Route
                component={SupportAppLegacy}
                path="/support"
                shouldRenderRoute={hasSupportAppPermission(session)}
              />
              <Route
                component={SupportApp}
                path="/supportnew"
                shouldRenderRoute={hasSupportAppPermission(session)}
              />
              <Route path="*">
                <NotFound />
              </Route>
            </Switch>
          ) : (
            <Switch>
              <Route exact component={NoTeamLandingPage} path="/noteam" />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={Profile}
                name="User Profile"
                path="/profile/:tab?"
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={TeamSettings}
                name="Team Settings"
                path="/settings/team/:teamId/:tab?"
                shouldRenderRoute={hasElevatedCompanyRole}
              />
              <TrackedGremlinRoute
                exact
                loggedIn
                component={CompanySettings}
                name="Company Settings"
                path="/settings/:tab?"
                shouldRenderRoute={hasElevatedCompanyRole}
              />
              {/* everything else redirects to /noteam */}
              <Redirect from={/^\/(?!profile)/} to="/noteam" />
            </Switch>
          )}
        </article>
        <Footer />
      </div>
    </div>
  )
}
